import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import { catchError, concatMap, debounceTime, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { UiPreferencesApiService } from '../../services/ui-preferences/ui-preferences-api.service';
import { AlertError, AlertSuccess } from '../actions/shared.actions';
import {
    DeleteUiPreferences,
    DeleteUiPreferencesSuccess,
    LoadUiPreferences,
    SaveUiPreferences,
    SaveUiPreferencesSuccess,
    SetUiPreferences,
    SetUiPreferencesError,
    SetUiPreferencesLoading,
} from '../actions/ui-preferences.actions';
import { getUiPreferencesLoaded$ } from '../selectors/ui-preferences.selector';

const LEGACY_STORAGE_KEY = 'user_preferences';
const LANDING_PAGE_PREFERENCE_KEY = 'landingPage';
const TASK_MANAGEMENT_PREFERENCE_KEY = 'taskManagement';
const DEFAULT_TENANT_ID_PREFERENCE_KEY = 'defaultTenantId';
const APPS_PREFERENCE_KEY = 'apps';

/** Read legacy localStorage data and return a preferences record, or null if empty */
function readLegacyLocalStorage(): Record<string, any> | null {
    try {
        const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const allUserPrefs: Record<string, any> = JSON.parse(raw);
        const result: Record<string, any> = {};
        for (const userId of Object.keys(allUserPrefs)) {
            const userPrefs = allUserPrefs[userId];
            if (userPrefs?.[LANDING_PAGE_PREFERENCE_KEY]) {
                result[LANDING_PAGE_PREFERENCE_KEY] = userPrefs[LANDING_PAGE_PREFERENCE_KEY];
            }
            if (userPrefs?.[DEFAULT_TENANT_ID_PREFERENCE_KEY]) {
                result[DEFAULT_TENANT_ID_PREFERENCE_KEY] = userPrefs[DEFAULT_TENANT_ID_PREFERENCE_KEY];
            }
            if (userPrefs?.[APPS_PREFERENCE_KEY]) {
                result[APPS_PREFERENCE_KEY] = {
                    ...(result[APPS_PREFERENCE_KEY] || {}),
                    ...userPrefs[APPS_PREFERENCE_KEY],
                };
            }
        }
        return Object.keys(result).length > 0 ? result : null;
    } catch {
        return null;
    }
}

function readLegacyUserConfiguration(): Record<string, any> | null {
    try {
        const raw = localStorage.getItem('user-configuration');
        if (!raw) {
            return null;
        }
        const userConfig = JSON.parse(raw);
        if (userConfig && Object.keys(userConfig).length > 0) {
            return userConfig;
        }
        return null;
    } catch {
        return null;
    }
}

function clearLegacyLocalStorage(): void {
    try {
        localStorage.removeItem(LEGACY_STORAGE_KEY);
        localStorage.removeItem('user-configuration');
    } catch {
        // ignore
    }
}

@Injectable()
export class UiPreferencesEffects {
    constructor(private actions$: Actions, private uiPreferencesApi: UiPreferencesApiService, private store$: Store) {}

    /** GET preferences from backend; skip if already loaded; perform one-time migration from localStorage if backend is empty */
    loadUiPreferences$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadUiPreferences),
            withLatestFrom(this.store$.select(getUiPreferencesLoaded$)),
            filter(([, loaded]) => !loaded),
            switchMap(() => {
                return from(this.uiPreferencesApi.getUiPreferences()).pipe(
                    switchMap(preferences => {
                        let prefsToSave: Record<string, any> | null = null;
                        const currentPrefs = preferences ? { ...preferences } : {};

                        const legacyStorage = readLegacyLocalStorage();
                        if (legacyStorage) {
                            if (legacyStorage[LANDING_PAGE_PREFERENCE_KEY] && !currentPrefs[LANDING_PAGE_PREFERENCE_KEY]) {
                                currentPrefs[LANDING_PAGE_PREFERENCE_KEY] = legacyStorage[LANDING_PAGE_PREFERENCE_KEY];
                                prefsToSave = currentPrefs;
                            }
                            if (legacyStorage[DEFAULT_TENANT_ID_PREFERENCE_KEY] && !currentPrefs[DEFAULT_TENANT_ID_PREFERENCE_KEY]) {
                                currentPrefs[DEFAULT_TENANT_ID_PREFERENCE_KEY] = legacyStorage[DEFAULT_TENANT_ID_PREFERENCE_KEY];
                                prefsToSave = currentPrefs;
                            }
                            if (legacyStorage[APPS_PREFERENCE_KEY] && !currentPrefs[APPS_PREFERENCE_KEY]) {
                                currentPrefs[APPS_PREFERENCE_KEY] = legacyStorage[APPS_PREFERENCE_KEY];
                                prefsToSave = currentPrefs;
                            }
                        }

                        const legacyUserConfig = readLegacyUserConfiguration();
                        if (legacyUserConfig && !currentPrefs[TASK_MANAGEMENT_PREFERENCE_KEY]) {
                            currentPrefs[TASK_MANAGEMENT_PREFERENCE_KEY] = legacyUserConfig.taskManagement || legacyUserConfig;
                            prefsToSave = currentPrefs;
                        }

                        // Clean up obsolete 'user-configuration' wrapper if present in backend response
                        if (currentPrefs['user-configuration']) {
                            if (!currentPrefs[TASK_MANAGEMENT_PREFERENCE_KEY] && currentPrefs['user-configuration']?.taskManagement) {
                                currentPrefs[TASK_MANAGEMENT_PREFERENCE_KEY] = currentPrefs['user-configuration'].taskManagement;
                            }
                            delete currentPrefs['user-configuration'];
                            prefsToSave = currentPrefs;
                        }

                        if (prefsToSave) {
                            clearLegacyLocalStorage();
                            return from(this.uiPreferencesApi.saveUiPreferences(prefsToSave)).pipe(
                                map(saved => SetUiPreferences({ preferences: saved })),
                                catchError(() => of(SetUiPreferences({ preferences: prefsToSave })))
                            );
                        }

                        clearLegacyLocalStorage();
                        return of(SetUiPreferences({ preferences: Object.keys(currentPrefs).length ? currentPrefs : null }));
                    }),
                    catchError(error => of(SetUiPreferencesError({ error: error?.msg || 'Failed to load UI preferences' })))
                );
            })
        );
    });

    /** POST preferences to backend */
    saveUiPreferences$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SaveUiPreferences),
            debounceTime(300),
            concatMap(action => {
                return from(this.uiPreferencesApi.saveUiPreferences(action.preferences)).pipe(
                    map(saved => SaveUiPreferencesSuccess({ preferences: saved, successMessage: action.successMessage })),
                    catchError(error => of(SetUiPreferencesError({ error: error?.msg || 'Failed to save UI preferences' })))
                );
            })
        );
    });

    /** DELETE all preferences on backend */
    deleteUiPreferences$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DeleteUiPreferences),
            mergeMap(() => {
                return from(this.uiPreferencesApi.deleteUiPreferences()).pipe(
                    map(() => DeleteUiPreferencesSuccess()),
                    catchError(error => of(SetUiPreferencesError({ error: error?.msg || 'Failed to delete UI preferences' })))
                );
            })
        );
    });

    /** Loading indicator: set true on any outgoing request action */
    setLoading$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadUiPreferences, SaveUiPreferences, DeleteUiPreferences),
            map(() => SetUiPreferencesLoading({ loading: true }))
        );
    });

    /** Loading indicator: clear on any terminal action */
    clearLoading$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetUiPreferences, SaveUiPreferencesSuccess, DeleteUiPreferencesSuccess, SetUiPreferencesError),
            map(() => SetUiPreferencesLoading({ loading: false }))
        );
    });

    /** Show alert on error */
    showError$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetUiPreferencesError),
            map(action => {
                if (action.error) {
                    return AlertError({ message: action.error });
                }
                return { type: 'NO_ACTION' };
            })
        );
    });

    /** Show success toast after save */
    showSaveSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SaveUiPreferencesSuccess),
            map(action => AlertSuccess({ message: action.successMessage || 'Preferences saved successfully' }))
        );
    });

    /** Show success toast after delete */
    showDeleteSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DeleteUiPreferencesSuccess),
            map(() => AlertSuccess({ message: 'Preferences reset to default' }))
        );
    });
}
